import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { SearchableSelect } from "@/shared/components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import type { Organization } from "../api/dataForwarderPolicies";
import { DataForwarderConnectionTestStatus } from "../types/preferences_types";


export function DataForwarderPolicyWizardDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;

  step: 1 | 2 | 3;
  onPrev: () => void;
  onNext: () => void;
  onCancel: () => void;
  onFinalize: () => void;

  errorMsg?: string;

  // step1
  organizations: Organization[];
  channels: string[];
  forwardingType: "s3" | "gcs";
  setForwardingType: (v: "s3" | "gcs") => void;
  policyName: string; setPolicyName: (v: string) => void;
  orgId: string; setOrgId: (v: string) => void;
  workspaceId: string; setWorkspaceId: (v: string) => void;
  channel: string; setChannel: (v: string) => void;
  subChannel: string; setSubChannel: (v: string) => void;
  dwAccountId: string; setDwAccountId: (v: string) => void;
  reports: string; setReports: (v: string) => void;
  externalId: string;

  // step2 - S3
  regions: string[];
  bucketRegion: string; setBucketRegion: (v: string) => void;
  bucketName: string; setBucketName: (v: string) => void;
  roleArn: string; setRoleArn: (v: string) => void;
  prefix: string; setPrefix: (v: string) => void;

  // step2 - GCS
  targetServiceAccount: string; setTargetServiceAccount: (v: string) => void;

  // step3
  connectionTestStatus: DataForwarderConnectionTestStatus;
  connectionTestMessage: string;
  onRunConnectionTest: () => void;
}) {
  const {
    open, onOpenChange, title,
    step, onPrev, onNext, onCancel, onFinalize,
    errorMsg,
  } = props;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {step === 1 && (() => {
          const hasChannel = !!props.channel;
          return (
          <div className="space-y-6">
            <div className="text-sm font-semibold">1단계: 전송 대상 설정</div>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-medium">정책 이름 <span className="text-destructive">*</span></div>
                  <Input value={props.policyName} onChange={(e) => props.setPolicyName(e.target.value)} maxLength={64} placeholder="정책을 구분할 이름" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">조직 <span className="text-destructive">*</span></div>
                  <SearchableSelect
                    value={props.orgId}
                    onValueChange={props.setOrgId}
                    options={props.organizations.map((org) => ({
                      value: org.id,
                      label: `${org.id} (${org.eng_identifier})`,
                    }))}
                    placeholder="조직을 선택하세요"
                  />
                </div>
              </div>
            </div>

            {/* 구분선 */}
            <div className="border-t" />

            <div className="space-y-4">
              <div>
                <div className="text-sm font-semibold text-foreground">필터링 조건 (선택)</div>
                <div className="text-xs text-muted-foreground mt-1">
                  특정 조건에 해당하는 데이터만 전송하려면 값을 입력하세요.
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-medium">워크스페이스 ID</div>
                  <Input value={props.workspaceId} onChange={(e) => props.setWorkspaceId(e.target.value)} maxLength={128} />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">채널</div>
                  <Select value={props.channel} onValueChange={props.setChannel}>
                    <SelectTrigger><SelectValue placeholder="채널을 선택하세요" /></SelectTrigger>
                    <SelectContent>
                      {props.channels.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className={`text-sm font-medium ${!hasChannel ? "text-muted-foreground" : ""}`}>
                    서브 채널
                    {!hasChannel && <span className="text-xs ml-1">(채널 선택 필요)</span>}
                  </div>
                  <Input
                    value={props.subChannel}
                    onChange={(e) => props.setSubChannel(e.target.value)}
                    maxLength={128}
                    disabled={!hasChannel}
                  />
                </div>

                <div className="space-y-2">
                  <div className={`text-sm font-medium ${!hasChannel ? "text-muted-foreground" : ""}`}>
                    DW Account ID
                    {!hasChannel && <span className="text-xs ml-1">(채널 선택 필요)</span>}
                  </div>
                  <Input
                    value={props.dwAccountId}
                    onChange={(e) => props.setDwAccountId(e.target.value)}
                    maxLength={128}
                    disabled={!hasChannel}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className={`text-sm font-medium ${!hasChannel ? "text-muted-foreground" : ""}`}>
                    전송 대상 리포트 타입 목록
                    {!hasChannel && <span className="text-xs ml-1">(채널 선택 필요)</span>}
                  </div>
                  <Input
                    value={props.reports}
                    onChange={(e) => props.setReports(e.target.value)}
                    maxLength={2048}
                    placeholder='["report1", "report2"]'
                    disabled={!hasChannel}
                  />
                  <div className="text-xs text-muted-foreground">
                    전송할 리포트 타입들을 JSON 배열로 입력.
                  </div>
                </div>
              </div>
            </div>

          </div>
          );
        })()}

        {step === 2 && (
          <div className="space-y-4">
            <div className="text-sm font-semibold">2단계: 전송 대상 스토리지 설정</div>

            <div className="space-y-2">
              <div className="text-sm font-medium">포워딩 타입 <span className="text-destructive">*</span></div>
              <RadioGroup
                value={props.forwardingType}
                onValueChange={(v: string) => props.setForwardingType(v as "s3" | "gcs")}
                className="flex flex-row gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="s3" id="s3" />
                  <Label htmlFor="s3">S3</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="gcs" id="gcs" />
                  <Label htmlFor="gcs">GCS</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="border-t" />

            {props.forwardingType === "s3" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-medium">S3 버킷 리전 <span className="text-destructive">*</span></div>
                  <Select value={props.bucketRegion} onValueChange={props.setBucketRegion}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {props.regions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">S3 버킷 이름 <span className="text-destructive">*</span></div>
                  <Input value={props.bucketName} onChange={(e) => props.setBucketName(e.target.value)} maxLength={64} />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">AWS Role ARN <span className="text-destructive">*</span></div>
                  <Input
                    value={props.roleArn}
                    onChange={(e) => props.setRoleArn(e.target.value)}
                    maxLength={256}
                    placeholder="arn:aws:iam::{account_id}:role/{role_name}"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">Path Prefix</div>
                  <Input value={props.prefix} onChange={(e) => props.setPrefix(e.target.value)} maxLength={128} />
                  <div className="text-xs text-muted-foreground">버킷 아래 특정 prefix 경로가 필요하면 입력</div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="border-t" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">External ID</div>
                  <Input value={props.externalId} readOnly className="bg-muted" />
                  <div className="text-xs text-muted-foreground">자동 생성된 External ID이며 수정 불가</div>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">GCS 버킷 이름 <span className="text-destructive">*</span></div>
                  <Input value={props.bucketName} onChange={(e) => props.setBucketName(e.target.value)} maxLength={64} />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">Target Service Account <span className="text-destructive">*</span></div>
                  <Input
                    value={props.targetServiceAccount}
                    onChange={(e) => props.setTargetServiceAccount(e.target.value)}
                    maxLength={256}
                    placeholder="service-account@project-id.iam.gserviceaccount.com"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium">Path Prefix</div>
                  <Input value={props.prefix} onChange={(e) => props.setPrefix(e.target.value)} maxLength={128} />
                  <div className="text-xs text-muted-foreground">버킷 아래 특정 prefix 경로가 필요하면 입력</div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="text-sm font-semibold">3단계: 연결 테스트 및 확인</div>
            <div className="text-sm text-muted-foreground">
              입력하신 정보로 {props.forwardingType === "s3" ? "S3" : "GCS"} 연결 테스트를 진행합니다.
            </div>

            <Button
              variant="outline"
              onClick={props.onRunConnectionTest}
              disabled={props.connectionTestStatus === "testing"}
            >
              {props.connectionTestStatus === "testing" && <LoadingSpinner size="sm" className="mr-2" />}
              연결 테스트 실행
            </Button>

            {props.connectionTestMessage && (
              <div className="text-sm">
                {props.connectionTestStatus === "success" ? (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
                    {props.connectionTestMessage}
                  </div>
                ) : props.connectionTestStatus === "failed" ? (
                  <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800">
                    {props.connectionTestMessage}
                  </div>
                ) : (
                  <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sky-800">
                    {props.connectionTestMessage}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {errorMsg && (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
            {errorMsg}
          </div>
        )}

        <DialogFooter className="mt-2 w-full">
          <div className="flex w-full items-center justify-between">
            {/* 왼쪽: 이전 */}
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={onPrev}>
                  이전
                </Button>
              )}
            </div>

            {/* 오른쪽: 취소 / 다음(또는 수정) */}
            <div className="flex items-center gap-2">
              {step < 3 ? (
                <>
                  <Button variant="ghost" onClick={onCancel}>
                    취소
                  </Button>
                  <Button onClick={onNext}>다음</Button>
                </>
              ) : (
                <Button onClick={onFinalize} disabled={props.connectionTestStatus !== "success"}>
                  완료
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
